/**
 * Voxium Script — French locale strings (fr, international French).
 *
 * Single source of truth for every French string rendered on /fr. Do not
 * hardcode French copy inside components — import from here so wording
 * fixes (pending native review) are a one-file change.
 *
 * Plain "fr" locale only — never "fr-CA". The target is standard
 * international French. Verbatim per Alain's brief; do not rewrite.
 */

const NBSP = "\u00A0";

// Thousands-group a whole number with a non-breaking space, e.g.
// 1290 -> "1\u00A0290", matching the French convention used in the brief
// ("1 290 $") and preventing the figure from breaking across a line.
const groupFR = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

// Format a whole-dollar amount in French convention: number, NBSP, "$".
export const priceFR = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `${groupFR(n)}${NBSP}$`;
};

// "ou 488 $/an, économisez 100 $" — numbers come from the same
// entitlements data the English page reads, never retyped here.
export const annualNoteFR = (monthly, annual) => {
  const m = Number(monthly);
  const a = Number(annual);
  if (!Number.isFinite(m) || !Number.isFinite(a)) return null;
  const save = m * 12 - a;
  if (save <= 0) return null;
  return `ou ${priceFR(a)}/an, économisez ${priceFR(save)}`;
};

export const fr = {
  locale: "fr",

  head: {
    title: "Voxium Script — Analyse de scénario pour les auteurs sérieux",
    description:
      "Analyse de scénario rigoureuse et honnête, et développement d'histoire. Mode Fondation, lecture du Conseil, Chef de studio. Conçu pour les auteurs qui visent la réunion.",
  },

  nav: {
    signIn: "Se connecter",
  },

  openingStrip: {
    slug1: "INT. SALLE DES AUTEURS — NUIT",
    action1: [
      "Une seule lampe brûle sur un bureau encombré de fiches.",
      "Des pages empilées. Un stylo, sans capuchon. Quelqu'un relit,",
      "encore une fois, le début d'un scénario sur lequel il",
      "travaille depuis des mois.",
    ],
    cue1: "LE LECTEUR",
    paren1: "(sans complaisance)",
    dialogue1: [
      "Le concept tient. Le protagoniste, pas encore.",
      "Vous nous demandez de suivre un personnage dont le désir",
      "n'est jamais clairement formulé sur la page. Précisez",
      "ce désir, et le reste de la structure pourra répondre.",
    ],
    slug2: "INT. BUREAU DU PRODUCTEUR — JOUR",
    action2: [
      "Le scénario est sur le bureau. Les marges sont couvertes de notes.",
      "Une seconde copie repose à côté, intacte, la page de titre",
      "légèrement froissée là où elle a été trop souvent lue.",
    ],
    cue2: "LE PRODUCTEUR",
    dialogue2: [
      "Trois actes qui fonctionnent. Un deuxième acte qui traîne",
      "sur huit pages. Un climax auquel j'ai cru avant que",
      "l'exposition ne l'ait mérité. Je recommande une réécriture, pas un simple polissage.",
    ],
    slug3: "EXT. LE PROJET FEU VERT — EN CONTINU",
    action3: [
      "La page tourne enfin, de son propre poids.",
      "Le travail est prêt. Le travail est prêt. Le travail est prêt.",
    ],
    cue3: "LE CONSEIL",
    paren3: "(à l'unisson)",
    dialogue3: ["Recommandé."],
    pageNumber: "17.",
  },

  hero: {
    headline: "Préparez votre scénario pour le feu vert.",
    body:
      "Tout auteur veut être produit. Voxium Script vous offre une analyse rigoureuse et honnête et un accompagnement de développement d'histoire, à un prix que les auteurs sérieux peuvent se permettre. Ainsi, quand vous obtiendrez la réunion, votre scénario sera prêt.",
    ctaPrimary: "Lancer ma première lecture",
    ctaSecondary: "Voir comment ça marche",
    attributionPrefix: "Par les créateurs de ",
    attributionBrand: "Voxium",
  },

  intro: {
    headline: "Où que vous en soyez dans le travail, nous vous rejoignons là.",
    body:
      "Le chemin de l'idée au scénario produit n'est pas linéaire. Certains jours, vous mettez un concept à l'épreuve. D'autres, vous peaufinez des dialogues pour une réunion d'analyse. Voxium Script est conçu pour vous rejoindre à chaque étape, avec le bon lecteur au bon moment.",
  },

  cards: [
    {
      mark: "I",
      eyebrow: "Développement d'histoire",
      headline: "Mettez l'idée à l'épreuve avant de l'écrire.",
      body:
        "Asseyez-vous avec le Mentor, un partenaire créatif impartial formé à l'art du récit. Parlez du concept, du protagoniste, de la question centrale. Faites apparaître tôt les problèmes porteurs, quand la révision ne coûte encore rien. Que vous façonniez un long métrage, construisiez un pilote ou testiez une logline, le Développement d'histoire est là où le travail commence.",
      bestFor:
        "les auteurs qui préfèrent trouver les fissures structurelles dans l'idée plutôt que dans la troisième version.",
      cta: "Commencer une séance de développement",
      testId: "card-story-development-fr",
    },
    {
      mark: "II",
      eyebrow: "Analyse de scénario",
      headline: "La salle lit. La salle tranche.",
      body: [
        `Importez votre scénario. Obtenez le Mode Fondation, un diagnostic de métier sur les quinze éléments qui font fonctionner les histoires. Puis réunissez le Conseil${NBSP}: le Lecteur, le Producteur, le Réalisateur, le Public. Chaque personnage lit depuis son propre fauteuil, avec sa propre voix. Ils ne s'accordent pas. Ils ne flattent pas. Vous entendez les questions avant que la salle ne les pose.`,
        `Si la salle dit oui, votre scénario mérite une lecture de plus${NBSP}: celle du Chef de studio. La perspective de celui qui mise. La voix qui demande non seulement «${NBSP}est-ce prêt${NBSP}?${NBSP}», mais «${NBSP}est-ce le genre d'histoire dans laquelle j'investirais maintenant${NBSP}?${NBSP}»`,
      ],
      bestFor:
        "les auteurs prêts à savoir si la version fonctionne, et quoi corriger si ce n'est pas le cas.",
      cta: "Lancer une analyse",
      testId: "card-script-coverage-fr",
    },
    {
      mark: "III",
      eyebrow: "Lectures courtes",
      headline: "Logline. Pitch. Synopsis. Traitement.",
      body:
        `Les documents qui ouvrent les portes sont les plus difficiles à bien écrire. Voxium Script évalue chaque document court selon les critères de métier qu'appliquent les professionnels${NBSP}: clarté en une phrase, enjeux qui font pencher un acheteur, structure visible à travers la prose. La logline, le dossier de pitch, le synopsis et le traitement partagent un seul compteur de lectures courtes. Utilisez-le avant de soumettre. Utilisez-le avant de pitcher.`,
      bestFor:
        "les auteurs qui préparent des documents devant convaincre en moins de trente secondes.",
      cta: "Évaluer un document",
      testId: "card-short-form-fr",
    },
  ],

  pricing: {
    eyebrow: "Tarifs",
    headline: "Abordable pour les auteurs sérieux, à chaque niveau.",
    tiers: [
      {
        slug: "free",
        name: "Gratuit",
        cadence: "à vie",
        body:
          "Une lecture du Conseil gratuite, plus une allocation de lectures Fondation et de lectures courtes. À vie, sans carte bancaire.",
        testId: "tier-free-fr",
      },
      {
        slug: "standard",
        name: "Standard",
        cadence: "par mois",
        body:
          "Une allocation mensuelle de lectures du Conseil, toutes les lectures courtes, et de généreuses lectures Fondation et écoutes audio chaque mois. Pour les auteurs en plein développement.",
        testId: "tier-standard-fr",
      },
      {
        slug: "pro",
        name: "Pro",
        cadence: "par mois",
        body:
          "Tout ce qui est inclus dans Standard, plus la Simulation de réunion de pitch et des limites mensuelles plus élevées dans tous les domaines. Pour les auteurs qui préparent la salle.",
        testId: "tier-pro-fr",
      },
      {
        slug: "pro_plus",
        name: "Pro Plus",
        cadence: "par mois",
        body: `Tout ce qui est inclus dans Pro, avec les limites mensuelles les plus élevées${NBSP}: la plus large allocation de séances du Conseil, de lectures Fondation, de questions de suivi et d'écoutes audio. Pour les auteurs qui mènent plusieurs projets de front.`,
        testId: "tier-pro-plus-fr",
      },
    ],
    qa: {
      eyebrow: "Questions de suivi (Q&R)",
      intro:
        "Des forfaits de questions de suivi à poser à un lecteur du Conseil sur une analyse que vous possédez déjà. Achetez une fois, utilisez quand vous voulez.",
      packs: [
        {
          slug: "qa_first_taste",
          name: "Premier Goût",
          body: "Trois questions de suivi. Offert une seule fois par compte.",
          testId: "qa-pack-first-taste-fr",
        },
        {
          slug: "qa_sip",
          name: "Gorgée",
          body: "Cinq questions de suivi.",
          testId: "qa-pack-sip-fr",
        },
        {
          slug: "qa_deep_dive",
          name: "Plongée",
          body: "Vingt questions de suivi.",
          testId: "qa-pack-deep-dive-fr",
        },
      ],
    },
    footnote: `Des forfaits supplémentaires sont offerts sur certains compteurs. Annulez à tout moment${NBSP}: votre travail, votre décision.`,
    currencyNote: "Tous les prix sont en dollars américains (USD).",
    cta: "Commencer gratuitement",
  },

  pitchMeeting: {
    eyebrow: "Répétez la salle avant d'y entrer",
    headline: "Simulation de réunion de pitch",
    body: `La salle est impitoyable. La simulation de réunion de pitch de Voxium Script vous place face à un Producteur, un Producteur exécutif et un Réalisateur. Vous pitchez${NBSP}: texte, présentation, traitement, ou les trois. Ils réagissent comme dans une vraie réunion. Ils posent les questions qu'on vous posera. Ils vous disent comment le projet se lit pour eux, et où ils résisteraient.`,
    frame: `Un verdict émulé selon les standards de l'industrie${NBSP}: un indicateur de la position de votre projet face à la salle, pas une garantie de vente. Chaque producteur est différent.`,
    closing: "Pitchez avant de pitcher.",
    cta: "Essayer une simulation de pitch",
  },

  founderQuote: {
    text:
      "J'ai construit cet outil pour que mes propres scénarios affrontent une critique honnête avant que la salle ne le fasse. Voxium Script vous dit ce qu'il voit, clairement, avec une voix, sans adoucir. C'est ça, le travail.",
    name: "Alain Raymond",
    role: "fondateur",
  },

  footer: {
    legal: {
      privacy: "Confidentialité",
      terms: "Conditions",
      refunds: "Remboursements",
      suffix: "(en anglais)",
    },
    // DRAFT — pending native review. Added 2026-10-21 per Alain's
    // referral-capture brief: discloses the vox_ref cookie in French.
    // Flagged for counsel re: Quebec Law 25 / consent, not decided here.
    referralNotice:
      "Si vous arrivez sur notre site par un lien partenaire, nous déposons un témoin nommé vox_ref pour créditer le partenaire qui vous a référé. Il conserve uniquement le code de référence — aucune donnée personnelle — pendant 90 jours.",
    copyright: "© 2026 RayMar & Co Enterprises LLC",
  },

  // Fallback-state copy — not part of Alain's homepage brief (no French
  // text was supplied for it), translated separately on request after
  // it was found rendering in English on /fr. Formal "vous" register.
  staleness: {
    prefix: "Les tarifs affichés reflètent nos dernières valeurs connues",
    notice:
      "notre synchronisation en direct avec l'application est momentanément indisponible. Connectez-vous sur",
    suffix: "pour consulter le catalogue et les limites actuels.",
  },

  // Header language picker — UI chrome string, not homepage copy, but
  // centralized here too so every /fr-visible string lives in one file.
  pickerAriaLabel: "Choisir la langue",
};

export default fr;
